import { useChatStore } from '@/store/useChatStore';
// Dynamic color imports - commented out to always use Inner Mastery Guide Blue
// import { getCategoryPalette, getTagGradient, hexToRgba } from '@/utils/colors';
import { hexToRgba } from '@/utils/colors';
import { ChatMode } from '@/utils/constants';
import { Icon } from '@iconify/react';
import { cn, Tooltip } from '@nextui-org/react';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

// Default accent color - purple/blue gradient (Counselor default)
const DEFAULT_GRADIENT =
  'linear-gradient(100deg, rgb(91, 143, 234), rgb(165, 148, 232))';
const DEFAULT_SIGNATURE_COLOR = '#5B8FEA';
const RECORDING_COLOR = '#ef4444'; // Red-500 for recording state
const RECORDING_GRADIENT = 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)';
const TEXT_MAX_HEIGHT = 128;
const SCROLL_NEAR_BOTTOM_THRESHOLD = 24;

const isNearBottom = (el: HTMLDivElement) =>
  el.scrollHeight - el.scrollTop - el.clientHeight <=
  SCROLL_NEAR_BOTTOM_THRESHOLD;

const adjustTextAreaHeight = (
  el: HTMLDivElement | null,
  pinnedRef: { current: boolean }
) => {
  if (!el) return;

  const overflows = el.scrollHeight > TEXT_MAX_HEIGHT;

  if (overflows) {
    el.style.height = `${TEXT_MAX_HEIGHT}px`;
  } else {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  if (pinnedRef.current) {
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }
};

const handleTextAreaScroll = (
  el: HTMLDivElement | null,
  pinnedRef: { current: boolean }
) => {
  if (!el) return;
  pinnedRef.current = isNearBottom(el);
};

interface TextModeInputProps {
  inputText: string;
  isListening: boolean;
  isLoading: boolean;
  isSubmitting?: boolean;
  onInputChange: (value: string) => void;
  onSendMessage: (text: string) => void;
  onToggleListening: () => void;
  onStopListeningForEdit?: () => void;
  setIsSettingsOpen: (open: boolean) => void;
  setChatMode: (mode: ChatMode) => void;
  onSaveToMemory: () => void;
  isSavingMemory?: boolean;
}

export function TextModeInput({
  inputText,
  isListening,
  isLoading,
  isSubmitting = false,
  onInputChange,
  onSendMessage,
  onToggleListening,
  onStopListeningForEdit,
  setChatMode,
}: TextModeInputProps) {
  const { setTypingOrListening, selectedPrompt } = useChatStore();
  const inputRef = useRef<HTMLDivElement>(null);
  const listeningTextRef = useRef<HTMLDivElement>(null);
  const wasListeningRef = useRef(false);
  const isListeningPinnedRef = useRef(true);
  const isInputPinnedRef = useRef(true);

  const hasText = inputText.trim().length > 0;
  const canSend = hasText && !isSubmitting;

  const adjustInputHeight = () =>
    adjustTextAreaHeight(inputRef.current, isInputPinnedRef);

  const adjustListeningHeight = () =>
    adjustTextAreaHeight(listeningTextRef.current, isListeningPinnedRef);

  const handleListeningTextScroll = () =>
    handleTextAreaScroll(listeningTextRef.current, isListeningPinnedRef);

  const handleInputTextScroll = () =>
    handleTextAreaScroll(inputRef.current, isInputPinnedRef);

  // Dynamic color feature - commented out to always use Inner Mastery Guide Blue
  // const { accentGradient, signatureColor } = useMemo(() => {
  //   const tag = selectedPrompt?.tag || 'Counselor';
  //   const palette = getCategoryPalette(tag);
  //   const gradient = getTagGradient(tag, false); // non-glassmorphic for solid look
  //   return {
  //     accentGradient: gradient || DEFAULT_GRADIENT,
  //     signatureColor: palette?.signature || DEFAULT_SIGNATURE_COLOR,
  //   };
  // }, [selectedPrompt?.tag]);

  // Always use Inner Mastery Guide Blue
  const accentGradient = DEFAULT_GRADIENT;
  const signatureColor = DEFAULT_SIGNATURE_COLOR;

  // Update typing/listening state
  useEffect(() => {
    setTypingOrListening(isListening || hasText);
  }, [isListening, hasText, setTypingOrListening]);

  // Sync contenteditable when not recording (including after speech → edit)
  useEffect(() => {
    if (!inputRef.current || isListening) return;

    const currentText = inputRef.current.textContent || '';
    const wasFocused = document.activeElement === inputRef.current;
    const shouldSync = inputText === '' || !wasFocused;

    if (shouldSync && currentText !== inputText) {
      inputRef.current.textContent = inputText;
      requestAnimationFrame(() => adjustInputHeight());
      if (wasFocused && inputText === '') {
        inputRef.current.focus();
        isInputPinnedRef.current = true;
      }
    }
  }, [inputText, isListening]);

  useEffect(() => {
    if (isListening) {
      requestAnimationFrame(() => adjustListeningHeight());
      return;
    }

    requestAnimationFrame(() => adjustInputHeight());
  }, [inputText, isListening]);

  useEffect(() => {
    if (isListening) {
      isListeningPinnedRef.current = true;
    }
  }, [isListening]);

  useEffect(() => {
    if (wasListeningRef.current && !isListening && hasText) {
      isInputPinnedRef.current = true;
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;

        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        adjustInputHeight();
      });
    }

    wasListeningRef.current = isListening;
  }, [isListening, hasText]);

  const inputContainerStyle = (borderColor: string, boxShadow: string) => ({
    border: `1px solid ${borderColor}`,
    boxShadow,
    transition: 'all 0.3s ease',
  });

  const handleInputContainerMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    e.currentTarget.style.borderColor = signatureColor;
    e.currentTarget.style.boxShadow = `0 6px 16px ${hexToRgba(signatureColor, 0.15)}`;
  };

  const handleInputContainerMouseLeave = (
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    if (!e.currentTarget.contains(document.activeElement)) {
      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
    }
  };

  const handleInputContainerFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    e.currentTarget.style.borderColor = signatureColor;
    e.currentTarget.style.boxShadow = `0 6px 16px ${hexToRgba(signatureColor, 0.15)}`;
  };

  const handleInputContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
    }
  };

  // Listening state UI component - matches reference design
  const ListeningUI = () => (
    <div className="flex items-center gap-2 w-full px-4 py-2.5 min-h-[52px]">
      {/* Recording indicator dot with pulse */}
      <div
        className="w-2.5 h-2.5 rounded-full flex-shrink-0 self-center"
        style={{
          backgroundColor: RECORDING_COLOR,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      {/* Sound wave bars */}
      <div className="flex items-center gap-0.5 h-6 flex-shrink-0 self-center">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="w-[3px] rounded-full"
            style={{
              backgroundColor: RECORDING_COLOR,
              animation: `soundwave 0.8s ease-in-out infinite`,
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </div>
      {/* Listening text or transcribed text */}
      <div
        ref={listeningTextRef}
        onScroll={handleListeningTextScroll}
        className="flex-1 min-w-0 max-h-32 overflow-y-auto text-base leading-6 text-gray-500 whitespace-pre-wrap break-words self-center"
      >
        {inputText || 'Listening...'}
      </div>
      {/* Stop recording and switch to editable text */}
      <Tooltip content="Stop recording and edit" placement="top">
        <button
          type="button"
          onClick={onStopListeningForEdit ?? onToggleListening}
          className="flex-shrink-0 self-center p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200"
          aria-label="Stop recording and edit"
        >
          <Icon icon="mdi:pencil-outline" width={20} />
        </button>
      </Tooltip>
    </div>
  );

  return (
    <div className="w-full">
      {/* Add keyframe animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        @keyframes soundwave {
          0%, 100% { height: 8px; }
          50% { height: 20px; }
        }
        @keyframes recordingGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(248, 113, 113, 0.6), 0 0 40px rgba(248, 113, 113, 0.3), 0 2px 12px rgba(248, 113, 113, 0.4);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 30px rgba(248, 113, 113, 0.8), 0 0 60px rgba(248, 113, 113, 0.4), 0 4px 16px rgba(248, 113, 113, 0.5);
            transform: scale(1.03);
          }
        }
        /* Contenteditable focus styles */
        [contenteditable]:focus {
          outline: none;
        }
      `}</style>
      <form
        className="flex w-full items-end gap-1.5"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) {
            onSendMessage(inputText.trim());
          }
        }}
      >
        {/* Input container - pill shape matching reference with Paper-like elevation */}
        <div className="flex-1 min-w-0">
          {isListening ? (
            <div
              className="relative w-full min-h-[52px] rounded-[26px] bg-white dark:bg-default-100"
              style={inputContainerStyle(
                'rgba(239, 68, 68, 0.3)',
                '0 4px 20px rgba(239, 68, 68, 0.15)'
              )}
            >
              <ListeningUI />
            </div>
          ) : (
            <div
              className="relative w-full min-h-[52px] bg-white dark:bg-default-100 rounded-[26px] flex items-stretch gap-1.5 transition-all duration-300 group"
              style={inputContainerStyle(
                'rgba(0, 0, 0, 0.08)',
                '0 4px 12px rgba(0, 0, 0, 0.08)'
              )}
              onMouseEnter={handleInputContainerMouseEnter}
              onMouseLeave={handleInputContainerMouseLeave}
              onFocus={handleInputContainerFocus}
              onBlur={handleInputContainerBlur}
            >
              <div className="flex items-center gap-1 px-4 py-2.5 w-full min-h-[52px]">
                {/* Voice mode toggle button - only shown when prompt has audio enabled */}
                {selectedPrompt?.enable_audio && (
                  <Tooltip content="Full Voice Mode" placement="top">
                    <button
                      type="button"
                      onClick={() => setChatMode(ChatMode.REAL_TIME)}
                      className="flex-shrink-0 pr-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                      aria-label="Toggle voice conversation mode"
                    >
                      <Icon icon="material-symbols:graphic-eq" width={20} />
                    </button>
                  </Tooltip>
                )}

                {/* Contenteditable input - bypasses autofill while enabling autocorrect */}
                <div className="relative flex-1 min-w-0 min-h-6">
                  {!hasText && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 flex items-center text-base leading-6 text-gray-400"
                    >
                      Type or tap mic to speak
                    </span>
                  )}
                  <div
                    ref={inputRef}
                    contentEditable
                    tabIndex={0}
                    role="textbox"
                    aria-label="Type or tap mic to speak"
                    aria-placeholder="Type or tap mic to speak"
                    className="min-h-6 max-h-32 overflow-y-auto bg-transparent border-none outline-none text-base leading-6 text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words"
                    autoCorrect="on"
                    autoCapitalize="sentences"
                    spellCheck={true}
                    suppressContentEditableWarning
                    onScroll={handleInputTextScroll}
                    onInput={(e: React.FormEvent<HTMLDivElement>) => {
                      const text = e.currentTarget.textContent || '';
                      onInputChange(text);
                      requestAnimationFrame(() => adjustInputHeight());
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      // Block all Enter key presses (send on Enter, prevent Shift+Enter line breaks)
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!e.shiftKey) {
                          const text =
                            e.currentTarget.textContent?.trim() || '';
                          if (text && !isSubmitting) {
                            onSendMessage(text);
                            // Clear handled by useEffect when inputText becomes ''
                          } else if (text) {
                            // Say why Enter did nothing; the text is kept.
                            toast.error(
                              'Please wait for the current response before sending another message.',
                              { id: 'send-blocked' }
                            );
                          }
                        }
                      }
                    }}
                    onPaste={(e: React.ClipboardEvent<HTMLDivElement>) => {
                      // Intercept paste to insert plain text only (no HTML/rich text)
                      e.preventDefault();
                      const text = e.clipboardData
                        .getData('text/plain')
                        .replace(/[\r\n]+/g, ' ');
                      // Insert at cursor position using Selection API (execCommand is deprecated)
                      const selection = window.getSelection();
                      if (selection && selection.rangeCount > 0) {
                        selection.deleteFromDocument();
                        selection
                          .getRangeAt(0)
                          .insertNode(document.createTextNode(text));
                        selection.collapseToEnd();
                      }
                      // Sync state
                      const newText = e.currentTarget.textContent || '';
                      onInputChange(newText);
                      requestAnimationFrame(() => adjustInputHeight());
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mic/Send button - circular, with dynamic gradient based on tag */}
        <Tooltip
          content={canSend ? 'Send' : 'Dictate'}
          placement="top"
          isDisabled={isLoading || isSubmitting}
        >
          <button
            type="button"
            disabled={
              isLoading || isSubmitting || (isListening && !hasText)
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (canSend) {
                onSendMessage(inputText.trim());
              } else if (!isSubmitting && !isListening) {
                onToggleListening();
              }
            }}
            className={cn(
              'w-[52px] h-[52px] min-w-[52px] flex-shrink-0 border-0 rounded-full',
              'flex items-center justify-center',
              'transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
              'hover:scale-105 active:scale-95',
              'disabled:opacity-50'
            )}
            style={{
              background: isListening ? RECORDING_GRADIENT : accentGradient,
              boxShadow: isListening
                ? '0 0 20px rgba(248, 113, 113, 0.6), 0 0 40px rgba(248, 113, 113, 0.3)'
                : `0 2px 8px ${hexToRgba(signatureColor, 0.3)}`,
              animation: isListening
                ? 'recordingGlow 1.2s ease-in-out infinite'
                : 'none',
            }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon
                className="text-white"
                icon={
                  hasText ? 'solar:arrow-up-linear' : 'mdi:microphone'
                }
                width={hasText ? 20 : 28}
              />
            )}
          </button>
        </Tooltip>
      </form>
    </div>
  );
}
